/**
 * Dimension `*D` block baking (docs/opencadstudio-parity/01-native-dxf-export.md
 * §3.2 decision 4). Like OCS's bake_dimension_blocks (explode.rs), the
 * WRITER side produces the anonymous dimension blocks the DXF format
 * expects the app to supply: every dimension entity gets a `*D<n>` block
 * whose geometry re-uses the exact math of the on-screen renderers
 * (DimensioningTools / SpecialDimensionRenderers), so the export matches
 * what the user sees. The DIMENSION entity itself only carries definition
 * points + measurement + the block name (code 2).
 *
 * Baked content is a set of virtual line/text/polyline entities with
 * deterministic ids (`dxf:<dimId>#<k>`) so handles stay session-stable.
 * Geometry is absolute model-space with a (0,0,0) block base — the classic
 * AutoCAD layout for anonymous dimension blocks.
 */

import type { Entity } from '../../../state/store';
import { useDimStyleStore, type DimensionStyle } from '../../../tools/cad/DimensioningTools';

export interface BakedDimensionBlock {
    /** Anonymous block name, e.g. '*D1'. */
    name: string;
    /** Virtual entities (line/text/polyline payloads), absolute coords. */
    entities: Entity[];
}

type Pt = number[];

/** Virtual entity factory — deterministic ids, never added to the ledger. */
function virtual(type: string, id: string, layer: string, data: Record<string, unknown>): Entity {
    return { id, type, name: `dxf-bake-${type}`, visible: true, locked: false, layer, data };
}

const lineEnt = (id: string, layer: string, a: Pt, b: Pt): Entity =>
    virtual('line', id, layer, { start: a, end: b });

const textEnt = (id: string, layer: string, at: Pt, content: string, height: number): Entity =>
    virtual('text', id, layer, { content, position: at, height, rotation: 0 });

const polyEnt = (id: string, layer: string, verts: Pt[]): Entity =>
    virtual('polyline', id, layer, { vertices: verts, closed: false });

const sub = (v: Pt, w: Pt): Pt => [v[0] - w[0], v[1] - w[1], (v[2] ?? 0) - (w[2] ?? 0)];
const add = (v: Pt, w: Pt): Pt => [v[0] + w[0], v[1] + w[1], (v[2] ?? 0) + (w[2] ?? 0)];
const mul = (v: Pt, k: number): Pt => [v[0] * k, v[1] * k, (v[2] ?? 0) * k];
const len = (v: Pt): number => Math.hypot(v[0], v[1]);
const norm = (v: Pt): Pt => {
    const l = len(v) || 1;
    return [v[0] / l, v[1] / l, 0];
};

/** Open arrowhead: two strokes from the tip at ±0.3 rad (ArrowHead parity). */
function arrow(stk: Entity[], id: string, layer: string, tip: Pt, angle: number, size: number): void {
    const b1 = [tip[0] - Math.cos(angle - 0.3) * size, tip[1] - Math.sin(angle - 0.3) * size, tip[2] ?? 0];
    const b2 = [tip[0] - Math.cos(angle + 0.3) * size, tip[1] - Math.sin(angle + 0.3) * size, tip[2] ?? 0];
    stk.push(lineEnt(`${id}#a1`, layer, b1, tip), lineEnt(`${id}#a2`, layer, tip, b2));
}

/** Dimension-line placement shared by the bake and the DIMENSION serializer. */
export function linearDimLinePoints(
    d: Record<string, any>, aligned: boolean
): { dimP1: Pt; dimP2: Pt; distance: number } | null {
    const p1 = d.point1 as Pt, p2 = d.point2 as Pt;
    if (!Array.isArray(p1) || !Array.isArray(p2)) return null;
    // Legacy payloads may lack a drop point — fall back to the first ref.
    const textP = (Array.isArray(d.textPosition) ? d.textPosition : p1) as Pt;
    let dimP1: Pt, dimP2: Pt;
    if (aligned) {
        const dir = norm(sub(p2, p1));
        const perp: Pt = [-dir[1], dir[0], 0];
        const off = sub(textP, p1);
        const perpDist = Math.abs(off[0] * perp[0] + off[1] * perp[1]);
        const sign = off[0] * perp[0] + off[1] * perp[1] >= 0 ? 1 : -1;
        dimP1 = add(p1, mul(perp, sign * perpDist));
        dimP2 = add(p2, mul(perp, sign * perpDist));
    } else {
        const horizontal = Math.abs(textP[1] - p1[1]) > Math.abs(textP[0] - p1[0]);
        if (horizontal) {
            dimP1 = [p1[0], textP[1], p1[2] ?? 0];
            dimP2 = [p2[0], textP[1], p2[2] ?? 0];
        } else {
            dimP1 = [textP[0], p1[1], p1[2] ?? 0];
            dimP2 = [textP[0], p2[1], p2[2] ?? 0];
        }
    }
    if (d.spaceOffset && d.spaceIndex) {
        const dir = norm(sub(dimP2, dimP1));
        const perp: Pt = [-dir[1], dir[0], 0];
        const shift = mul(perp, d.spaceOffset * d.spaceIndex);
        dimP1 = add(dimP1, shift);
        dimP2 = add(dimP2, shift);
    }
    const distance = Math.hypot(dimP2[0] - dimP1[0], dimP2[1] - dimP1[1]);
    return { dimP1, dimP2, distance };
}

/** Radial anchor points shared by the bake and the DIMENSION serializer. */
export function radialPoints(d: Record<string, any>, diameter: boolean): { a: Pt; b: Pt; radius: number } | null {
    const center = d.center as Pt, radPt = d.radiusPoint as Pt;
    if (!Array.isArray(center) || !Array.isArray(radPt)) return null;
    const radius = typeof d.radius === 'number' ? d.radius : Math.hypot(radPt[0] - center[0], radPt[1] - center[1]);
    if (diameter) {
        const opposite: Pt = [center[0] - (radPt[0] - center[0]), center[1] - (radPt[1] - center[1]), center[2] ?? 0];
        return { a: opposite, b: radPt, radius };
    }
    return { a: center, b: radPt, radius };
}

/** Linear-family geometry (linear/aligned/continue/baseline renderers). */
function bakeLinear(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle, aligned: boolean): void {
    const p1 = d.point1 as Pt, p2 = d.point2 as Pt;
    if (!Array.isArray(p1) || !Array.isArray(p2)) return;
    const pts = linearDimLinePoints(d, aligned);
    if (!pts) return;
    const { dimP1, dimP2, distance } = pts;
    // Extension lines (start at the measured point, overshoot past the dim line).
    const ext1 = [p1, add(dimP1, mul(norm(sub(dimP1, p1)), style.extensionOvershoot))];
    const ext2 = [p2, add(dimP2, mul(norm(sub(dimP2, p2)), style.extensionOvershoot))];
    stk.push(lineEnt(`${id}#e1`, layer, ext1[0], ext1[1]), lineEnt(`${id}#e2`, layer, ext2[0], ext2[1]));
    // Dimension line (split by the DIMBREAK gap when flagged).
    const angle = Math.atan2(dimP2[1] - dimP1[1], dimP2[0] - dimP1[0]);
    if (d.breakAtCrossing) {
        const dir = norm(sub(dimP2, dimP1));
        const mid = mul(add(dimP1, dimP2), 0.5);
        const g = Math.min(style.arrowSize * 1.5, (distance / 2) * 0.4);
        stk.push(lineEnt(`${id}#d1`, layer, dimP1, sub(mid, mul(dir, g / 2))));
        stk.push(lineEnt(`${id}#d2`, layer, add(mid, mul(dir, g / 2)), dimP2));
    } else {
        stk.push(lineEnt(`${id}#d`, layer, dimP1, dimP2));
    }
    arrow(stk, id, layer, dimP1, angle, style.arrowSize);
    arrow(stk, id, layer, dimP2, angle + Math.PI, style.arrowSize);
    // Label (midpoint, lifted by the text gap like the renderer).
    const measured = aligned ? Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) : distance;
    const label = String(d.override ?? `${(measured * style.scale).toFixed(style.precision)}${style.suffix}`);
    const mid = mul(add(dimP1, dimP2), 0.5);
    stk.push(textEnt(`${id}#t`, layer, [mid[0], mid[1], (mid[2] ?? 0) + style.textGap], label, style.textHeight));
}

/** Radial geometry (radius/diameter renderers). */
function bakeRadial(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle, diameter: boolean): void {
    const pts = radialPoints(d, diameter);
    if (!pts) return;
    const { a, b, radius } = pts;
    const center = d.center as Pt, radPt = d.radiusPoint as Pt;
    const angle = Math.atan2(radPt[1] - center[1], radPt[0] - center[0]);
    stk.push(lineEnt(`${id}#d`, layer, a, b));
    arrow(stk, id, layer, radPt, angle + Math.PI, style.arrowSize);
    const mid = diameter ? ([...center] as Pt) : mul(add(center, radPt), 0.5);
    const prefix = diameter ? 'Ø' : 'R';
    const label = String(d.override ?? `${prefix}${(radius * style.scale).toFixed(style.precision)}${style.suffix}`);
    stk.push(textEnt(`${id}#t`, layer, [mid[0], mid[1], (mid[2] ?? 0) + style.textGap], label, style.textHeight));
}

/** Angular geometry: two rays + measurement arc + degree label. */
function bakeAngular(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle): void {
    const c = d.center as Pt, p1 = d.point1 as Pt, p2 = d.point2 as Pt;
    if (!Array.isArray(c) || !Array.isArray(p1) || !Array.isArray(p2)) return;
    const a0 = Math.atan2(p1[1] - c[1], p1[0] - c[0]);
    let sweep = Math.atan2(p2[1] - c[1], p2[0] - c[0]) - a0;
    while (sweep <= 0) sweep += Math.PI * 2;
    const r1 = Math.max(Math.hypot(p1[0] - c[0], p1[1] - c[1]), 1e-4);
    const r2 = Math.max(Math.hypot(p2[0] - c[0], p2[1] - c[1]), 1e-4);
    const arcR = Math.min(r1, r2) * 0.45;
    stk.push(lineEnt(`${id}#r1`, layer, c, p1), lineEnt(`${id}#r2`, layer, c, p2));
    const n = Math.max(8, Math.round(sweep / (Math.PI / 24)));
    const verts: Pt[] = [];
    for (let i = 0; i <= n; i++) {
        const a = a0 + (sweep * i) / n;
        verts.push([c[0] + Math.cos(a) * arcR, c[1] + Math.sin(a) * arcR, c[2] ?? 0]);
    }
    stk.push(polyEnt(`${id}#arc`, layer, verts));
    const midA = a0 + sweep / 2;
    const tp: Pt = [
        c[0] + Math.cos(midA) * (arcR + style.textHeight),
        c[1] + Math.sin(midA) * (arcR + style.textHeight),
        (c[2] ?? 0) + style.textGap,
    ];
    const deg = typeof d.angle === 'number' ? d.angle : (sweep * 180) / Math.PI;
    const label = String(d.override ?? `${deg.toFixed(Math.max(0, style.precision - 1))}°`);
    stk.push(textEnt(`${id}#t`, layer, tp, label, style.textHeight));
}

/** Arc-length geometry: the tessellated path + end arrows + label. */
function bakeArcLength(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle): void {
    const path = (Array.isArray(d.arcPts) && d.arcPts.length >= 2 ? d.arcPts : [d.point1, d.point2]) as Pt[];
    if (!Array.isArray(path) || path.length < 2) return;
    stk.push(polyEnt(`${id}#p`, layer, path));
    const dir = norm(sub(path[path.length - 1], path[path.length - 2]));
    const ang = Math.atan2(dir[1], dir[0]);
    arrow(stk, id, layer, path[0], ang + Math.PI, style.arrowSize);
    arrow(stk, id, layer, path[path.length - 1], ang, style.arrowSize);
    const tp = d.textPosition as Pt;
    if (Array.isArray(tp)) {
        const label = String(d.override ?? (typeof d.arcLength === 'number' ? d.arcLength.toFixed(style.precision) : ''));
        stk.push(textEnt(`${id}#t`, layer, [tp[0], tp[1], (tp[2] ?? 0) + style.textGap], label, style.textHeight));
    }
}

/** Ordinate geometry: feature→label leader + datum stub + value. */
function bakeOrdinate(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle): void {
    const feature = d.point1 as Pt, end = (d.point2 ?? d.textPosition) as Pt;
    if (!Array.isArray(feature) || !Array.isArray(end)) return;
    stk.push(lineEnt(`${id}#l`, layer, feature, end));
    const isX = d.axis === 'x';
    const stub = isX ? [end[0], end[1] + 0.35, end[2] ?? 0] : [end[0] + 0.35, end[1], end[2] ?? 0];
    stk.push(lineEnt(`${id}#s`, layer, end, stub));
    const tp = isX ? [end[0], end[1] + 0.1, (end[2] ?? 0) + style.textGap] : [end[0] + 0.1, end[1], (end[2] ?? 0) + style.textGap];
    const label = String(d.override ?? '');
    stk.push(textEnt(`${id}#t`, layer, tp, `${isX ? 'X' : 'Y'} ${label}`, style.textHeight));
}

/** Jogged radius geometry: center→jog zigzag→rim + R label. */
function bakeJogged(stk: Entity[], id: string, layer: string, d: Record<string, any>, style: DimensionStyle): void {
    const center = d.center as Pt, rim = d.radiusPoint as Pt, jog = (d.textPosition ?? d.radiusPoint) as Pt;
    if (!Array.isArray(center) || !Array.isArray(rim)) return;
    const dir = norm(sub(jog, center));
    const perp: Pt = [-dir[1], dir[0], 0];
    const p = mul(perp, 0.08);
    const t = 0.55;
    const base = add(mul(center, 1 - t), mul(rim, t));
    const z1 = add(base, p);
    const z2 = sub(base, p);
    const jogEnd = add(base, mul(dir, Math.hypot(rim[0] - center[0], rim[1] - center[1]) * 0.06));
    const ang = Math.atan2(rim[1] - center[1], rim[0] - center[0]);
    stk.push(
        lineEnt(`${id}#c`, layer, center, base),
        lineEnt(`${id}#z1`, layer, base, z1),
        lineEnt(`${id}#z2`, layer, z1, z2),
        lineEnt(`${id}#z3`, layer, z2, jogEnd),
        lineEnt(`${id}#r`, layer, jogEnd, rim),
    );
    arrow(stk, id, layer, rim, ang, style.arrowSize);
    const value = typeof d.radius === 'number' ? d.radius : Math.hypot(rim[0] - center[0], rim[1] - center[1]);
    const label = String(d.override ?? `R${(value * style.scale).toFixed(style.precision)}${style.suffix}`);
    const mid = add(mul(center, 0.38), mul(rim, 0.62));
    stk.push(textEnt(`${id}#t`, layer, [mid[0], mid[1], (mid[2] ?? 0) + style.textGap + 0.05], label, style.textHeight));
}

/** Tolerance frame geometry: bordered cells + cell texts. */
function bakeTolerance(stk: Entity[], id: string, layer: string, d: Record<string, any>): void {
    const cells = (Array.isArray(d.cells) ? d.cells : ['±0.0']) as string[];
    const pos = (d.position as Pt) ?? [0, 0, 0];
    const h = (d.height as number) || 0.3;
    const widths = cells.map((c) => Math.max(h * 1.2, 0.28 + String(c).length * h * 0.55));
    const total = widths.reduce((a, b) => a + b, 0);
    const p = (dx: number, dy: number): Pt => [pos[0] + dx, pos[1] + dy, pos[2] ?? 0];
    stk.push(
        lineEnt(`${id}#b1`, layer, p(0, 0), p(total, 0)),
        lineEnt(`${id}#b2`, layer, p(total, 0), p(total, h)),
        lineEnt(`${id}#b3`, layer, p(total, h), p(0, h)),
        lineEnt(`${id}#b4`, layer, p(0, h), p(0, 0)),
    );
    let x = 0;
    cells.forEach((c, i) => {
        const w = widths[i];
        if (i > 0) stk.push(lineEnt(`${id}#c${i}`, layer, p(x, 0), p(x, h)));
        stk.push(textEnt(`${id}#t${i}`, layer, [pos[0] + x + w / 2, pos[1] + h / 2, (pos[2] ?? 0) + 0.005], String(c), h * 0.62));
        x += w;
    });
}

/**
 * Bake `*D` blocks for every dimension entity in the list. Returns a map
 * keyed by the dimension entity id; `name` is unique per export.
 */
export function bakeDimensionBlocks(entities: Entity[]): Map<string, BakedDimensionBlock> {
    const styles = useDimStyleStore.getState();
    const out = new Map<string, BakedDimensionBlock>();
    let counter = 0;
    entities.forEach((e) => {
        if (e.type !== 'dimension') return;
        const d = e.data as Record<string, any>;
        const style = styles.getStyleFor(typeof d.styleId === 'string' ? d.styleId : undefined);
        const stack: Entity[] = [];
        const id = `dxf:${e.id}`;
        switch (d.type) {
            case 'linear': bakeLinear(stack, id, e.layer, d, style, false); break;
            case 'aligned':
            case 'continue':
            case 'baseline': bakeLinear(stack, id, e.layer, d, style, true); break;
            case 'radius': bakeRadial(stack, id, e.layer, d, style, false); break;
            case 'diameter': bakeRadial(stack, id, e.layer, d, style, true); break;
            case 'angular': bakeAngular(stack, id, e.layer, d, style); break;
            case 'arcLength': bakeArcLength(stack, id, e.layer, d, style); break;
            case 'ordinate': bakeOrdinate(stack, id, e.layer, d, style); break;
            case 'jogged': bakeJogged(stack, id, e.layer, d, style); break;
            case 'tolerance': bakeTolerance(stack, id, e.layer, d); break;
            default: bakeLinear(stack, id, e.layer, d, style, true); break;
        }
        if (stack.length === 0) return;
        counter += 1;
        out.set(e.id, { name: `*D${counter}`, entities: stack });
    });
    return out;
}
